// Lib
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

type BackgroundRemovalWipeOverlay = {
    dataUrl: string;
    renderKey: number;
    wiping: boolean;
    // The rotate/flip transform the outgoing image was displayed with, so the wipe frame mirrors the cropper
    // instead of showing the raw (un-flipped) source pixels.
    transform: string;
};

type UseBackgroundRemovalWipeOverlayArgs = {
    enabled: boolean;
    editorImageLoadKey: string;
    editorIsReady: boolean;
    imageTransform: string;
};

/**
 * Wipes the previously-loaded editor image away once the replacement cropper image is ready.
 */
export const useBackgroundRemovalWipeOverlay = ({
    enabled,
    editorImageLoadKey,
    editorIsReady,
    imageTransform,
}: UseBackgroundRemovalWipeOverlayArgs) => {
    const lastLoadedDataUrlRef = useRef<string | null>(null);
    const previousEditorImageLoadKeyRef = useRef(editorImageLoadKey);
    const wipeRenderKeyRef = useRef(0);
    const [wipeOverlay, setWipeOverlay] = useState<BackgroundRemovalWipeOverlay | null>(null);

    useLayoutEffect(() => {
        const previousEditorImageLoadKey = previousEditorImageLoadKeyRef.current;
        previousEditorImageLoadKeyRef.current = editorImageLoadKey;

        if (previousEditorImageLoadKey === editorImageLoadKey) return;
        if (!enabled) return;
        if (!lastLoadedDataUrlRef.current) return;

        wipeRenderKeyRef.current += 1;
        setWipeOverlay({
            dataUrl: lastLoadedDataUrlRef.current,
            renderKey: wipeRenderKeyRef.current,
            wiping: false,
            transform: imageTransform,
        });
    }, [enabled, editorImageLoadKey, imageTransform]);

    useEffect(() => {
        if (!editorIsReady) return undefined;
        if (!wipeOverlay) return undefined;
        if (wipeOverlay.wiping) return undefined;

        const { renderKey } = wipeOverlay;

        // useEffect runs after the overlay is committed, so the initial clip-path is in place before the wipe class is added.
        setWipeOverlay((current) => (current?.renderKey === renderKey ? { ...current, wiping: true } : current));
    }, [editorIsReady, wipeOverlay]);

    const handleImageDataUrlLoad = useCallback((dataUrl: string) => {
        lastLoadedDataUrlRef.current = dataUrl;
    }, []);

    const handleWipeAnimationEnd = useCallback(() => {
        setWipeOverlay((current) => (current?.wiping ? null : current));
    }, []);

    return {
        handleImageDataUrlLoad,
        handleWipeAnimationEnd,
        wipeOverlay,
    };
};
